import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { DialogModule } from '@syncfusion/ej2-angular-popups';

export type ConfirmationDialogMode = 'ok' | 'yes-no';
export type ConfirmationDialogTone = 'info' | 'warning' | 'error' | 'success';

@Component({
    selector: 'app-confirmation-dialog',
    standalone: true,
    imports: [CommonModule, DialogModule, ButtonModule],
    templateUrl: './confirmation-dialog.component.html',
    styleUrls: ['./confirmation-dialog.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationDialogComponent implements OnChanges {
    @Input() visible = true;
    @Input() title = 'Aviso';
    @Input() message = '';
    @Input() mode: ConfirmationDialogMode = 'ok';
    @Input() tone: ConfirmationDialogTone = 'info';
    @Input() confirmButtonText = 'OK';
    @Input() cancelButtonText = 'Cancelar';
    @Input() closeOnOverlayClick = false;

    @Output() resolved = new EventEmitter<boolean>();

    @ViewChild('messageElement') private messageElement?: ElementRef<HTMLElement>;

    isSingleLineMessage = false;
    isLayoutReady = false;
    private pendingResolution: boolean | null = null;

    constructor(private cdr: ChangeDetectorRef) { }

    get dialogWidth(): string {
        const lines = this.message.split(/\r?\n/).filter((line) => line.trim().length > 0);
        const longestLineLength = Math.max(...lines.map((line) => line.length), this.title.length, 18);
        const estimatedWidth = Math.min(760, Math.max(320, 280 + (longestLineLength * 5.2)));
        const viewportWidth = typeof window === 'undefined' ? 1024 : window.innerWidth;
        const viewportPadding = viewportWidth <= 640 ? 24 : 48;
        const responsiveWidth = Math.max(280, viewportWidth - viewportPadding);

        return `${Math.round(Math.min(estimatedWidth, responsiveWidth))}px`;
    }

    get iconClass(): string {
        switch (this.tone) {
            case 'warning':
                return 'fa fa-exclamation-triangle';
            case 'error':
                return 'fa fa-times-circle';
            case 'success':
                return 'fa fa-check-circle';
            default:
                return 'fa fa-info-circle';
        }
    }

    get dialogCssClass(): string {
        return `app-confirmation-dialog app-confirmation-dialog--${this.tone}`;
    }

    get contentCssClass(): string {
        return `confirmation-content confirmation-content--${this.tone}${this.isSingleLineMessage ? ' is-single-line' : ''}${this.isLayoutReady ? ' is-layout-ready' : ''}`;
    }

    get confirmButtonCssClass(): string {
        switch (this.tone) {
            case 'warning':
                return 'e-warning';
            case 'error':
                return 'e-danger';
            case 'success':
                return 'e-success';
            default:
                return 'e-info';
        }
    }

    get isYesNoMode(): boolean {
        return this.mode === 'yes-no';
    }

    onConfirm(): void {
        this.requestClose(true);
    }

    onCancel(): void {
        this.requestClose(false);
    }

    onClose(): void {
        const resolution = this.pendingResolution ?? false;
        this.pendingResolution = null;
        this.resolved.emit(resolution);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && !changes['visible'].currentValue) {
            this.isSingleLineMessage = false;
            this.isLayoutReady = false;
            this.cdr.markForCheck();
        }

        if (changes['message'] && !this.visible) {
            this.isSingleLineMessage = false;
            this.isLayoutReady = false;
            this.cdr.markForCheck();
        }
    }

    onOpen(): void {
        this.isLayoutReady = false;
        this.cdr.markForCheck();
        this.updateSingleLineState();
    }

    private updateSingleLineState(): void {
        queueMicrotask(() => {
            const messageElement = this.messageElement?.nativeElement;
            if (!messageElement) {
                return;
            }

            const computedStyle = window.getComputedStyle(messageElement);
            const lineHeight = Number.parseFloat(computedStyle.lineHeight);

            if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
                this.isSingleLineMessage = false;
                this.isLayoutReady = true;
                this.cdr.markForCheck();
                return;
            }

            const renderedHeight = Math.ceil(messageElement.getBoundingClientRect().height);
            const normalizedLineHeight = Math.ceil(lineHeight);
            const isSingleLine = renderedHeight <= normalizedLineHeight + 2;

            this.isSingleLineMessage = isSingleLine;
            this.isLayoutReady = true;
            this.cdr.markForCheck();
        });
    }

    private requestClose(resolution: boolean): void {
        this.pendingResolution = resolution;
        this.visible = false;
        this.cdr.markForCheck();
    }
}